import { Injectable, OnDestroy, signal } from '@angular/core';
import type MapView from '@arcgis/core/views/MapView';
import type SceneView from '@arcgis/core/views/SceneView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Sketch from '@arcgis/core/widgets/Sketch';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Graphic from '@arcgis/core/Graphic';
import { FlightDrawTool } from './flight-state';

@Injectable()
export class FlightService implements OnDestroy {
  readonly graphicsVersion = signal(0);
  private sketch: Sketch | null = null;
  private _graphicsLayer: GraphicsLayer | null = null;
  private createHandle: { remove: () => void } | null = null;

  get graphicsLayer(): GraphicsLayer | null {
    return this._graphicsLayer;
  }

  initialize(view: MapView | SceneView, existingLayer?: GraphicsLayer): void {
    this.createHandle?.remove();
    this.createHandle = null;
    this.sketch?.destroy();
    this.sketch = null;

    // Create a fresh graphics layer attached to the current view.map.
    // If an existing layer was passed (from a previous view), copy its graphics into the new layer so drawings persist when switching 2D <-> 3D.
    const createLayer = () => new GraphicsLayer({ elevationInfo: { mode: 'on-the-ground' as const } });
    if (existingLayer) {
      const newLayer = createLayer();
      try {
        const srcGraphicsCol: any = (existingLayer as any).graphics;
        let items: any[] = [];
        if (srcGraphicsCol) {
          if (typeof srcGraphicsCol.toArray === 'function') items = srcGraphicsCol.toArray();
          else if (Array.isArray(srcGraphicsCol.items)) items = srcGraphicsCol.items.slice();
          else if (Array.isArray(srcGraphicsCol)) items = srcGraphicsCol.slice();
        }
        for (const g of items) {
          if (!g) continue;
          try {
            // prefer clone(), or serialize/deserialize to ensure a fresh graphic instance
            if (typeof (g.clone) === 'function') {
              newLayer.add(g.clone());
            } else if (typeof (g.toJSON) === 'function') {
              try { newLayer.add(new Graphic(g.toJSON())); } catch { newLayer.add(g); }
            } else {
              newLayer.add(g);
            }
          } catch {
            try { newLayer.add(g); } catch { /* ignore individual graphic failures */ }
          }
        }
      } catch {
        // copying failed — continue with an empty new layer
      }
      view.map?.add(newLayer);
      this._graphicsLayer = newLayer;
    } else {
      this._graphicsLayer = createLayer();
      view.map?.add(this._graphicsLayer);
    }

    // Símbolo transparente para que no se vea mientras dibuja
    const transparentLine = new SimpleLineSymbol({
      color: [0, 0, 0, 0],
      width: 2,
      style: 'solid',
    });

    this.sketch = new Sketch({
      layer: this._graphicsLayer,
      view,
      polylineSymbol: transparentLine,
      visibleElements: {
        createTools: {
          point: false, polyline: false, polygon: false, circle: false,
          rectangle: false, text: false, multipoint: false,
          freehandPolygon: false, freehandPolyline: false,
        },
        selectionTools: { 'lasso-selection': false, 'rectangle-selection': false },
        undoRedoMenu: false,
      } as any,
    });
  }

  activateTool(tool: FlightDrawTool, onComplete?: (graphic?: Graphic) => void): void {
    if (!this.sketch) return;
    this.sketch.cancel();
    this.createHandle?.remove();

    if (tool === 'polyline-freehand') {
      this.sketch.create('polyline', { mode: 'freehand' });
    } else {
      this.sketch.create('polyline');
    }

    this.createHandle = this.sketch.on('create', (event: any) => {
      if (event.state === 'complete') {
        this.sketch?.cancel();
        this.graphicsVersion.update(v => v + 1);
        onComplete?.(event.graphic);
      }
    });
  }

  deactivate(): void {
    this.sketch?.cancel();
  }

  clear(): void {
    this._graphicsLayer?.removeAll();
    this.graphicsVersion.update(v => v + 1);
  }

  destroy(): void {
    this.createHandle?.remove();
    this.createHandle = null;
    this.sketch?.destroy();
    this.sketch = null;
    if (this._graphicsLayer) {
      this._graphicsLayer.removeAll();
    }
    this._graphicsLayer = null;
  }

  ngOnDestroy(): void {
    this.destroy();
  }
}